/**
 * net/errors.ts — o ÚNICO erro que sai da camada de rede do receptor.
 *
 * POR QUE UM TIPO só: o resto do receptor (casca, player, cache) nunca deve fazer
 * `if (status === 401)`. Ele decide pelo `code`, que é a decisão do contrato-base
 * §2: "Decida pela `code`, nunca pela `message`". Uma `message` de API pode mudar
 * amanhã (o backend traduz tudo para PT-BR e é o app que mostra); um `code` não.
 *
 * `status` fica só para diagnóstico/log — nunca para decidir comportamento.
 */

export type GatewayErrorCode =
  /** A tela foi excluída no app: apagar credenciais e voltar ao pareamento (§10). */
  | 'DEVICE_TOKEN_REVOKED'
  /** Token desconhecido/inválido — mesma reação do receptor, mas por outro motivo. */
  | 'DEVICE_TOKEN_INVALID'
  /** Sem rede, DNS, timeout, CORS. Nunca é motivo para mostrar erro na TV. */
  | 'NETWORK'
  /** Recurso não existe (mídia removida, sessão de pareamento morta). */
  | 'NOT_FOUND'
  /** 429 do pareamento/da API: backoff, nunca crash. */
  | 'RATE_LIMITED'
  /** Código/sessão de pareamento venceu: gerar outro sozinho, sem interação. */
  | 'PAIRING_EXPIRED'
  /** Vínculo por QR: a solicitação venceu (5 min) ou foi encerrada. */
  | 'LINK_REQUEST_EXPIRED'
  /** Vínculo por QR: o `X-Link-Secret` não bate com a solicitação. */
  | 'INVALID_LINK_SECRET'
  /** 200 que não é o payload esperado (contrato furado, proxy com HTML). */
  | 'BAD_RESPONSE';

export class GatewayError extends Error {
  readonly code: GatewayErrorCode;
  readonly status?: number;

  constructor(code: GatewayErrorCode, message: string, status?: number) {
    super(message);
    this.name = 'GatewayError';
    this.code = code;
    if (status !== undefined) {
      this.status = status;
    }
  }

  /**
   * A tela deixou de existir do lado do servidor. Os dois códigos de device token
   * compartilham a reação (PLAYER.md §2) e o chamador NÃO precisa saber qual foi.
   */
  get credentialIsGone(): boolean {
    return this.code === 'DEVICE_TOKEN_REVOKED' || this.code === 'DEVICE_TOKEN_INVALID';
  }
}

/**
 * Mapeia `code` do envelope `{error:{code,message}}` + status HTTP para o nosso
 * código. Regra do contrato-base §2: o `code` manda; o status é só o desempate
 * para quando o envelope não veio (proxy, 500, HTML de erro).
 *
 * Códigos do backend que NÃO são device token caem todos em NETWORK/BAD_RESPONSE
 * de propósito: um `VALIDATION_ERROR` do backend é bug do servidor, não um evento
 * que a TV precise tratar — ela não pode mostrar erro técnico ao cliente (§5.2).
 */
export function codeFromErrorEnvelope(
  envelopeCode: string | undefined,
  status: number,
): GatewayErrorCode {
  switch (envelopeCode) {
    case 'DEVICE_TOKEN_REVOKED':
      return 'DEVICE_TOKEN_REVOKED';
    case 'DEVICE_TOKEN_INVALID':
    case 'UNAUTHENTICATED':
      return 'DEVICE_TOKEN_INVALID';
    case 'INVALID_PAIRING_SECRET':
      return 'DEVICE_TOKEN_INVALID';
    // Vínculo por QR (plano §1.4): o segredo errado é da MESMA família do
    // segredo de pareamento — a solicitação morre e a TV cria outra.
    case 'INVALID_LINK_SECRET':
      return 'INVALID_LINK_SECRET';
    case 'LINK_REQUEST_EXPIRED':
      return 'LINK_REQUEST_EXPIRED';
    case 'SESSION_EXPIRED':
    case 'PAIRING_CODE_EXPIRED':
      return 'PAIRING_EXPIRED';
    case 'RATE_LIMITED':
      return 'RATE_LIMITED';
    case 'NOT_FOUND':
    case 'SCREEN_NOT_FOUND':
    case 'MEDIA_NOT_FOUND':
    case 'PAIRING_CODE_NOT_FOUND':
      return 'NOT_FOUND';
    default:
      break;
  }
  // Sem código reconhecível: decide pelo status, nunca pela mensagem.
  if (status === 401) return 'DEVICE_TOKEN_INVALID';
  if (status === 404) return 'NOT_FOUND';
  if (status === 410) return 'LINK_REQUEST_EXPIRED';
  if (status === 429) return 'RATE_LIMITED';
  // 5xx/0/408: para o receptor é indistinguível de "não deu" — same treatment.
  if (status === 0 || status === 408 || status >= 500) return 'NETWORK';
  return 'BAD_RESPONSE';
}
